/**
 * Settings — rendered from the JSON Schema stored with each row.
 *
 * The point of this screen is that it has no per-setting code. Adding a new
 * parameter means inserting a row with a `schemaValor`; the form appears on its
 * own. Hard-coding one form per setting would have made "EGBA can tune it"
 * true only for the settings someone remembered to build a screen for.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'

import { api, type Configuracao, type Item, type Lista } from '../api/cliente'
import {
  Alternador,
  Aviso,
  Botao,
  Campo,
  Carregando,
  Cartao,
  Etiqueta,
  Modal,
  Selecao,
  formatarData,
} from '../componentes/ui'

interface PropriedadeSchema {
  type?: string
  title?: string
  description?: string
  enum?: string[]
  format?: string
  minimum?: number
  maximum?: number
  items?: { type?: string }
}

const NOME_GRUPO: Record<string, string> = {
  compliance: 'Compliance',
  institucional: 'Institucional',
  lgpd: 'Proteção de dados',
  marca: 'Identidade visual',
  marketing: 'Medição e remarketing',
  operacao: 'Operação',
  seguranca: 'Segurança',
  geral: 'Geral',
}

/** Campos que o servidor devolve mascarados — ver `mascararSegredos` na API. */
const CAMPO_SEGREDO = /secret|senha|password|token|apiKey|api_key/i
const MASCARA = '••••••••'

/** One field, chosen from the JSON Schema type. */
function CampoDinamico({
  nome,
  esquema,
  valor,
  aoMudar,
}: {
  nome: string
  esquema: PropriedadeSchema
  valor: unknown
  aoMudar: (v: unknown) => void
}) {
  const rotulo = esquema.title ?? nome

  if (CAMPO_SEGREDO.test(nome)) {
    const guardado = valor === MASCARA
    return (
      <Campo
        rotulo={rotulo}
        type="password"
        autoComplete="new-password"
        placeholder={guardado ? 'Guardada — digite para substituir' : ''}
        // Mostrar a máscara como valor faria a pessoa apagar oito bolinhas
        // achando que estava apagando a chave. O campo fica vazio e o
        // servidor mantém o que já tem quando nada é digitado.
        value={guardado ? '' : String(valor ?? '')}
        onChange={(e) => aoMudar(e.target.value === '' ? MASCARA : e.target.value)}
        ajuda={
          guardado
            ? `${esquema.description ?? ''} Há uma chave guardada; deixe em branco para mantê-la.`.trim()
            : esquema.description
        }
      />
    )
  }

  if (esquema.type === 'boolean') {
    return (
      <Alternador
        rotulo={rotulo}
        ajuda={esquema.description}
        checked={Boolean(valor)}
        onChange={aoMudar}
      />
    )
  }

  if (esquema.enum) {
    return (
      <Selecao
        rotulo={rotulo}
        ajuda={esquema.description}
        value={String(valor ?? '')}
        onChange={(e) => aoMudar(e.target.value)}
        opcoes={esquema.enum.map((v) => ({ valor: v, texto: v }))}
      />
    )
  }

  if (esquema.type === 'integer' || esquema.type === 'number') {
    return (
      <Campo
        rotulo={rotulo}
        ajuda={esquema.description}
        type="number"
        min={esquema.minimum}
        max={esquema.maximum}
        value={String(valor ?? '')}
        onChange={(e) => aoMudar(e.target.value === '' ? null : Number(e.target.value))}
      />
    )
  }

  if (esquema.type === 'array') {
    // Uma linha por item — mais direto que um editor de lista para telefone e
    // afins, que é o único array que existe hoje.
    const lista = Array.isArray(valor) ? (valor as string[]) : []
    return (
      <Campo
        rotulo={rotulo}
        ajuda={`${esquema.description ?? ''} Separe por vírgula.`.trim()}
        value={lista.join(', ')}
        onChange={(e) =>
          aoMudar(
            e.target.value
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean),
          )
        }
      />
    )
  }

  return (
    <Campo
      rotulo={rotulo}
      ajuda={esquema.description}
      type={esquema.format === 'date' ? 'date' : esquema.format === 'email' ? 'email' : 'text'}
      value={String(valor ?? '')}
      onChange={(e) => aoMudar(e.target.value)}
    />
  )
}

export function Configuracoes() {
  const qc = useQueryClient()
  const [editando, setEditando] = useState<Configuracao | null>(null)
  const [rascunho, setRascunho] = useState<Record<string, unknown>>({})
  const [erro, setErro] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['configuracoes'],
    queryFn: () => api.get<Lista<Configuracao>>('/api/v1/admin/configuracoes'),
  })

  const salvar = useMutation({
    mutationFn: (c: Configuracao) =>
      api.put<Item<Configuracao>>(`/api/v1/admin/configuracoes/${c.chave}`, { valor: rascunho }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['configuracoes'] })
      // A janela eleitoral aparece no cabeçalho — precisa refletir na hora.
      void qc.invalidateQueries({ queryKey: ['eleitoral'] })
      fechar()
    },
    onError: (e: Error) => setErro(e.message),
  })

  function abrir(c: Configuracao) {
    setEditando(c)
    setRascunho((c.valor as Record<string, unknown>) ?? {})
    setErro(null)
  }

  function fechar() {
    setEditando(null)
    setErro(null)
  }

  function aoEnviar(e: FormEvent) {
    e.preventDefault()
    setErro(null)
    if (editando) salvar.mutate(editando)
  }

  const configuracoes = data?.dados ?? []
  const grupos = [...new Set(configuracoes.map((c) => c.grupo))]

  const propriedades = (editando?.schemaValor?.properties ?? {}) as Record<string, PropriedadeSchema>
  const obrigatorios = (editando?.schemaValor?.required ?? []) as string[]

  if (isLoading) return <Carregando />

  return (
    <div className="space-y-6">
      {grupos.map((grupo) => (
        <Cartao key={grupo} titulo={NOME_GRUPO[grupo] ?? grupo}>
          <ul className="divide-y divide-slate-100">
            {configuracoes
              .filter((c) => c.grupo === grupo)
              .map((c) => (
                <li key={c.id} className="flex items-start justify-between gap-6 py-4 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900">{c.rotulo}</p>
                      {c.sensivel && <Etiqueta tom="perigo">sensível</Etiqueta>}
                    </div>
                    {c.descricao && (
                      <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-500">
                        {c.descricao}
                      </p>
                    )}
                    <p className="mt-1.5 font-mono text-xs text-slate-400">
                      {c.chave} · atualizado em {formatarData(c.atualizadoEm)}
                    </p>
                  </div>
                  <Botao variante="secundario" tamanho="sm" onClick={() => abrir(c)}>
                    Editar
                  </Botao>
                </li>
              ))}
          </ul>
        </Cartao>
      ))}

      <Modal
        aberto={Boolean(editando)}
        aoFechar={fechar}
        largura="lg"
        titulo={editando?.rotulo ?? ''}
        descricao={editando?.descricao ?? undefined}
      >
        {editando && (
          <form onSubmit={aoEnviar} className="space-y-5">
            {Object.keys(propriedades).length === 0 ? (
              <Aviso tom="atencao">
                Esta configuração não tem um esquema declarado, então não há formulário para
                exibir. Cadastre <code>schemaValor</code> na linha para editá-la por aqui.
              </Aviso>
            ) : (
              Object.entries(propriedades).map(([nome, esquema]) => (
                <CampoDinamico
                  key={nome}
                  nome={obrigatorios.includes(nome) ? `${nome} *` : nome}
                  esquema={esquema}
                  valor={rascunho[nome]}
                  aoMudar={(v) => setRascunho((r) => ({ ...r, [nome]: v }))}
                />
              ))
            )}

            {editando.chave === 'compliance.eleitoral' && (
              <Aviso tom="atencao" titulo="Antes de habilitar a exceção de mercado">
                A exceção do art. 73, VI, “b” só pode ser ligada com parecer jurídico da EGBA, e a
                referência do parecer é obrigatória — a plataforma recusa a alteração sem ela.
              </Aviso>
            )}

            {erro && <Aviso tom="perigo">{erro}</Aviso>}

            <div className="flex justify-end gap-3 border-t border-slate-200 pt-4">
              <Botao type="button" variante="secundario" onClick={fechar}>
                Cancelar
              </Botao>
              <Botao type="submit" carregando={salvar.isPending}>
                Salvar
              </Botao>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}
